import type { CSSProperties } from "react";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import { MarkdownProse } from "./MarkdownProse";
import {
  FIELD_PLACEHOLDER_CHIP_CLASS,
  isMarkdownQuoteField,
  quoteFieldLabel,
  resolveQuoteField,
} from "./quoteFields";
import type { FpCommonProps } from "./types";

interface FpFieldProps extends FpCommonProps {
  source: string | null;
}

/**
 * Renders `<fp-field source="...">`: the bound quote field as inline text. A
 * field the quote leaves empty or cannot bind never reaches the customer - it
 * has nothing to show, and a placeholder marker in a printed document is worse
 * than a blank - but it keeps a placeholder chip in the editor, so it stays
 * visible and selectable there.
 *
 * The value renders in a span, so a container style that sizes or boxes the
 * field promotes it to an inline block, which is what lets a signature field
 * carry its own font, size and alignment instead of the inherited body text.
 * @param props - The field source and common fp-* props
 * @returns The resolved field value, a placeholder chip, or null when empty
 */
export function FpField({
  source,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpFieldProps) {
  const { quote, editable } = useQuoteDocumentContext();
  const value = resolveQuoteField(quote, source);
  if ((value === null || value === "") && !editable) {
    return null;
  }
  if (value === null || value === "") {
    return (
      <span
        className={`${FIELD_PLACEHOLDER_CHIP_CLASS} ${className ?? ""}`}
        style={fieldSpanStyle(style)}
        data-fp-node={dataFpNode}
      >
        {`⟨${quoteFieldLabel(source) ?? "Empty field"}⟩`}
      </span>
    );
  }
  if (isMarkdownQuoteField(source)) {
    return (
      <MarkdownProse
        markdown={value}
        className={className}
        classMode="extend"
        style={style}
        data-fp-node={dataFpNode}
        fieldTitle={
          editable ? (quoteFieldLabel(source) ?? undefined) : undefined
        }
      />
    );
  }
  return (
    <span
      className={className}
      style={fieldSpanStyle(style)}
      data-fp-node={dataFpNode}
      data-field-title={
        editable ? (quoteFieldLabel(source) ?? undefined) : undefined
      }
    >
      {value}
    </span>
  );
}

/** Style properties an inline box ignores or renders badly. */
const BOX_STYLE_PROPERTIES: readonly (keyof CSSProperties)[] = [
  "width",
  "height",
  "minHeight",
  "padding",
  "backgroundColor",
  "border",
  "borderRadius",
  "textAlign",
];

/**
 * The value span's style, promoted to an inline block when the container style
 * sets a property a plain inline span would drop.
 * @param style - The container style, or undefined when the field has none
 * @returns The span's style, or undefined when no style was set
 */
export function fieldSpanStyle(
  style: CSSProperties | undefined,
): CSSProperties | undefined {
  if (style === undefined || !needsBoxDisplay(style)) {
    return style;
  }
  return { display: "inline-block", ...style };
}

/**
 * Whether a container style sets a property that needs a block formatting box.
 * @param style - The container style
 * @returns True when the span has to become an inline block
 */
function needsBoxDisplay(style: CSSProperties): boolean {
  return BOX_STYLE_PROPERTIES.some((property) => style[property] !== undefined);
}
